import type {
  Building,
  LoadCountry,
  LoadSettings,
  ProjectSettings,
  SnowZone,
  Terrain,
  WindZone,
} from './types.js';

/**
 * Roof load (kN/m²) the frame table and the beams are sized for without a snow zone:
 * about 0.85 kN/m² snow on the roof and 0.5 kN/m² own weight.
 */
export const DEFAULT_ROOF_LOAD = 1.35;
/** Own weight of a sandwich or trapezoidal sheet roof with its purlins. */
export const ROOF_DEAD_LOAD = 0.5;
/** Snow on a roof of up to 30° pitch compared with the ground (shape coefficient μ1). */
export const ROOF_SNOW_SHAPE = 0.8;
/** Photovoltaic modules with their mounting on the roof (about 20 kg/m²). */
export const PV_LOAD = 0.2;
/**
 * Peak velocity pressure (kN/m²) the members are sized for without a wind zone: wind zone 2
 * inland up to 10 m (DIN EN 1991-1-4/NA, simplified).
 */
export const DEFAULT_WIND_PRESSURE = 0.65;

export const LOAD_COUNTRIES = ['de', 'pl'] as const satisfies readonly LoadCountry[];

/** Zones of each country's snow and wind maps and the terrain choices of its wind rules. */
export const SNOW_ZONES: Readonly<Record<LoadCountry, readonly SnowZone[]>> = {
  de: ['1', '1a', '2', '2a', '3'],
  pl: ['1', '2', '3', '4', '5'],
};
export const WIND_ZONES: Readonly<Record<LoadCountry, readonly WindZone[]>> = {
  de: ['1', '2', '3', '4'],
  pl: ['1', '2', '3'],
};
export const TERRAINS: Readonly<Record<LoadCountry, readonly Terrain[]>> = {
  de: ['inland', 'coast'],
  pl: ['open', 'suburban', 'urban'],
};

export const loadCountry = (loads: LoadSettings | undefined): LoadCountry => loads?.country ?? 'de';

/** Ground snow s_k = a + b · ((A + 140) / 760)², at least `min` (DIN EN 1991-1-3/NA). */
const DE_SNOW: Readonly<Record<string, { a: number; b: number; min: number; factor: number }>> = {
  '1': { a: 0.19, b: 0.91, min: 0.65, factor: 1 },
  '1a': { a: 0.19, b: 0.91, min: 0.65, factor: 1.25 },
  '2': { a: 0.25, b: 1.91, min: 0.85, factor: 1 },
  '2a': { a: 0.25, b: 1.91, min: 0.85, factor: 1.25 },
  '3': { a: 0.31, b: 2.91, min: 1.1, factor: 1 },
};

/** Ground snow in Poland (PN-EN 1991-1-3, national annex) at altitude `a`. */
function polishSnow(zone: SnowZone, a: number): number {
  switch (zone) {
    case '1':
      return Math.max(0.7, 0.007 * a - 1.4);
    case '2':
      return 0.9;
    case '3':
      return Math.max(1.2, 0.006 * a - 0.6);
    case '4':
      return 1.6;
    default:
      return Math.max(2, 0.93 * Math.exp(0.00134 * a));
  }
}

/** Characteristic snow load on the ground (kN/m²) of a zone at `altitude` metres above sea level. */
export function groundSnowLoad(
  zone: SnowZone,
  altitude: number,
  country: LoadCountry = 'de',
): number {
  const a = Math.max(0, altitude);
  if (country === 'pl') return polishSnow(zone, a);
  const z = DE_SNOW[zone] ?? DE_SNOW['2'];
  if (!z) return 0.85;
  const k = (a + 140) / 760;
  return Math.max(z.min, z.a + z.b * k * k) * z.factor;
}

/** Snow on the roof (kN/m²): from the project's zone, else the usual value of the tables. */
export function roofSnowLoad(loads: LoadSettings | undefined): number {
  return loads
    ? ROOF_SNOW_SHAPE * groundSnowLoad(loads.snowZone, loads.altitude, loadCountry(loads))
    : DEFAULT_ROOF_LOAD - ROOF_DEAD_LOAD;
}

/**
 * Simplified peak velocity pressures in Germany (DIN EN 1991-1-4/NA, table NA.B.3) for
 * buildings up to 10, 18 and 25 m: inland and at the coast.
 */
const DE_WIND: Readonly<Record<WindZone, { inland: number[]; coast: number[] }>> = {
  '1': { inland: [0.5, 0.65, 0.75], coast: [0.5, 0.65, 0.75] },
  '2': { inland: [0.65, 0.8, 0.9], coast: [0.85, 1, 1.1] },
  '3': { inland: [0.8, 0.95, 1.1], coast: [1.05, 1.2, 1.3] },
  '4': { inland: [0.95, 1.15, 1.3], coast: [1.25, 1.4, 1.55] },
};

/** Poland (PN-EN 1991-1-4, national annex): basic wind speed and exposure by terrain. */
const PL_WIND_SPEED: Readonly<Record<WindZone, number>> = { '1': 22, '2': 26, '3': 22, '4': 22 };
const PL_EXPOSURE: Readonly<Record<string, { c: number; k: number; zMin: number }>> = {
  open: { c: 2.3, k: 0.24, zMin: 5 },
  suburban: { c: 1.9, k: 0.26, zMin: 10 },
  urban: { c: 1.5, k: 0.29, zMin: 15 },
};

/** Peak velocity pressure q_p (kN/m²) at `height` above ground for the project's site. */
export function peakWindPressure(loads: LoadSettings | undefined, height: number): number {
  const h = Math.max(0, height);
  const band = h <= 10 ? 0 : h <= 18 ? 1 : 2;
  if (!loads || loadCountry(loads) === 'de') {
    const zone = DE_WIND[loads?.windZone ?? '2'];
    const row = loads?.terrain === 'coast' ? zone.coast : zone.inland;
    return row[band] ?? DEFAULT_WIND_PRESSURE;
  }
  const zone = loads.windZone ?? '1';
  const above = Math.max(0, Math.min(1000, loads.altitude) - 300);
  const v = PL_WIND_SPEED[zone] * (1 + 0.0006 * above);
  const qb = (0.5 * 1.25 * v * v) / 1000;
  const e = PL_EXPOSURE[loads.terrain ?? 'suburban'] ?? PL_EXPOSURE['suburban'];
  if (!e) return DEFAULT_WIND_PRESSURE;
  return qb * e.c * (Math.max(h, e.zMin) / 10) ** e.k;
}

/** Loads a hall's members are sized for (kN/m²). */
export interface HallLoads {
  /** Snow, own weight and photovoltaics on the roof. */
  readonly roof: number;
  /** Peak velocity pressure of the wind at the ridge. */
  readonly wind: number;
}

export const DEFAULT_HALL_LOADS: HallLoads = {
  roof: DEFAULT_ROOF_LOAD,
  wind: DEFAULT_WIND_PRESSURE,
};

/** Load on a hall roof without photovoltaics (snow and own weight, kN/m²). */
export function hallRoofLoad(settings: Pick<ProjectSettings, 'loads'> | undefined): number {
  const loads = settings?.loads;
  return loads ? roofSnowLoad(loads) + ROOF_DEAD_LOAD : DEFAULT_ROOF_LOAD;
}

/** Roof and wind loads of a building under the project's site loads. */
export function hallLoads(
  settings: Pick<ProjectSettings, 'loads'> | undefined,
  b: Pick<Building, 'eaveHeight' | 'pv'> & { readonly ridge?: number },
): HallLoads {
  return {
    roof: hallRoofLoad(settings) + (b.pv ? PV_LOAD : 0),
    wind: peakWindPressure(settings?.loads, b.ridge ?? b.eaveHeight),
  };
}
